import {PermissionType} from '@hconnect/apiclient'
import {FlexPage} from '@hconnect/common/components'
import {LocationSelectOption} from '@hconnect/common/hproduce/types'
import {Error404Page, PageTitle} from '@hconnect/uikit/src/lib2'
import {plantIdsByPlantNameCompareFn, useUpmPlantsContext} from '@hconnect/upm'
import {useTheme, useMediaQuery, Stack, CircularProgress, Box} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import {generatePath, Link, Navigate, useNavigate, useParams} from 'react-router'
import useResizeObserver from 'use-resize-observer'

import {AreaKilnStatusMap, AreaSelectBreadcrumbMultiplePermissions} from '../components'
import {AREA_DASHBOARD_WIDGET_NAMESPACE, SUPPORTED_MAP_AREAS} from '../consts'
import {PlantMapData, useMapAreaConfig} from '../hooks'
import {MapWithCardsDesktopLayout, MapWithCardsMobileLayout} from '../layouts'
import {getDisplayBounds} from '../utils'

export type AreaRouteSegments = {
  area: string
  plant: string
}

type TitleProps = {
  title: React.ReactNode
  breadcrumbs?: React.ReactNode
}

const Title = ({title, breadcrumbs}: TitleProps) => {
  return (
    <Stack>
      {breadcrumbs}
      <PageTitle ml={0}>{title}</PageTitle>
    </Stack>
  )
}

type AreaDashboardWrapperProps = {
  dashboardTitle: string
  dashboardRoute: string
  routeSegments: AreaRouteSegments
  permission: PermissionType
  useMapMarkersDataHook: (areaId?: string) => {
    data: PlantMapData[]
    isInitialLoading: boolean
    isError: boolean
    refetch: () => void
  }
  useAccessHook: (permission: PermissionType) => {allowedPlants: string[]}
  allowedAreas?: string[]
  getAreaById?: (areaId: string) => {id: string; name: string}
  headerActionContent?: React.ReactNode
  fallbackBreadcrumbs?: React.ReactNode
  getPlantCards: (args: {
    plantIds: string[]
    spacing?: number
    columnsCount?: number
    kpiGridItemXs?: number
  }) => React.ReactNode
}

const DESKTOP_CARDS_SECTION_WIDTH = 600

const getMapWithSideCardsDisplayBounds = (width: number, height: number) =>
  getDisplayBounds(width - DESKTOP_CARDS_SECTION_WIDTH, height)

export const AreaDashboardContainer = ({
  dashboardTitle,
  dashboardRoute,
  routeSegments,
  permission,
  useMapMarkersDataHook,
  useAccessHook,
  getPlantCards,
  allowedAreas,
  getAreaById,
  headerActionContent,
  fallbackBreadcrumbs
}: AreaDashboardWrapperProps) => {
  const {areaId} = useParams<{areaId: string}>()
  const upmPlants = useUpmPlantsContext()
  const navigate = useNavigate()
  const {t} = useTranslation(AREA_DASHBOARD_WIDGET_NAMESPACE)
  const {allowedPlants} = useAccessHook(permission)
  const {data} = useMapMarkersDataHook(areaId)
  const filteredMapData = data?.filter(({plantId}) => allowedPlants.includes(plantId)) ?? []
  const equipmentPlantIds = useMemo(() => data?.map(({plantId}) => plantId) ?? [], [data])
  const {mapAreaConfig, isMapConfigLoading} = useMapAreaConfig(areaId, equipmentPlantIds)
  const {ref: containerRef, width = 0, height = 0} = useResizeObserver()
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))
  const isMedium = useMediaQuery(theme.breakpoints.between('sm', 'lg'))

  const sortedPlantCardsIds = filteredMapData
    .map(({plantId}) => plantId)
    .sort(plantIdsByPlantNameCompareFn(upmPlants))

  const isMapReady = width > 0 && height > 0

  const handlePlantClick = (plantId: string) => {
    void navigate(`${routeSegments.plant}/${plantId}`)
  }

  const areasOptions: LocationSelectOption[] =
    allowedAreas?.map((areaId) => {
      const area = getAreaById?.(areaId)

      return {
        id: areaId,
        name: area?.name ?? '',
        url: `${routeSegments.area}/${areaId}`
      }
    }) ?? []

  const commonFlexPageProps = {
    title: (
      <Title
        title={dashboardTitle}
        breadcrumbs={
          areasOptions.length ? (
            <AreaSelectBreadcrumbMultiplePermissions
              areasOptions={areasOptions}
              areaRouteSegment={routeSegments.area}
              sx={{ml: 0}}
            />
          ) : (
            fallbackBreadcrumbs
          )
        }
      />
    ),
    nameHeaderSx: {mt: 0},
    headerActionContent
  }

  const notFoundPage = (
    <Error404Page
      caption={t('errorPage.notFound')}
      description={t('errorPage.notFoundDescription')}
      goToCaption={t('action.backToTarget', {targetName: dashboardTitle})}
      goToLink={generatePath(dashboardRoute)}
      RouterLink={Link}
    />
  )

  if (!areaId) {
    return notFoundPage
  }

  if (!SUPPORTED_MAP_AREAS.includes(areaId)) {
    return <Navigate to={routeSegments.plant} />
  }

  if (isMapConfigLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    )
  }

  if (!mapAreaConfig) {
    return notFoundPage
  }

  if (isSmall) {
    return (
      <FlexPage {...commonFlexPageProps} nameBodySx={{px: 0, pb: 1}}>
        <MapWithCardsMobileLayout
          mapContainerRef={containerRef}
          mapSlot={
            isMapReady && (
              <AreaKilnStatusMap
                width={width}
                height={height}
                plantMapData={filteredMapData}
                displayBounds={getDisplayBounds(width, height)}
                onPlantMarkerClick={handlePlantClick}
                mapAreaConfig={mapAreaConfig}
              />
            )
          }
          cardsSlot={getPlantCards({
            plantIds: sortedPlantCardsIds,
            columnsCount: 1,
            kpiGridItemXs: 6
          })}
        />
      </FlexPage>
    )
  }

  if (isMedium) {
    return (
      <FlexPage
        {...commonFlexPageProps}
        headerActionContent={headerActionContent}
        nameBodySx={{px: 0, pb: 1}}
      >
        <MapWithCardsMobileLayout
          mapContainerRef={containerRef}
          mapSlot={
            isMapReady && (
              <AreaKilnStatusMap
                width={width}
                height={height}
                plantMapData={filteredMapData}
                displayBounds={getDisplayBounds(width, height)}
                onPlantMarkerClick={handlePlantClick}
                mapAreaConfig={mapAreaConfig}
              />
            )
          }
          cardsSlot={getPlantCards({
            plantIds: sortedPlantCardsIds,
            columnsCount: 2,
            kpiGridItemXs: 6
          })}
        />
      </FlexPage>
    )
  }

  return (
    <FlexPage
      {...commonFlexPageProps}
      useViewportHeight
      headerActionContent={headerActionContent}
      nameBodySx={{px: 0}}
    >
      <MapWithCardsDesktopLayout
        mapContainerRef={containerRef}
        mapSlot={
          isMapReady && (
            <AreaKilnStatusMap
              width={width}
              height={height}
              plantMapData={filteredMapData}
              displayBounds={getMapWithSideCardsDisplayBounds(width, height)}
              onPlantMarkerClick={handlePlantClick}
              mapAreaConfig={mapAreaConfig}
            />
          )
        }
        cardsSlot={getPlantCards({
          plantIds: sortedPlantCardsIds,
          columnsCount: 1,
          kpiGridItemXs: 4
        })}
        cardsSlotWidth={DESKTOP_CARDS_SECTION_WIDTH}
      />
    </FlexPage>
  )
}
